/**
 * Points → per-player deltas.
 *
 * Two settlement shapes, chosen per game via `settings.mode`:
 *
 *  - Serious mode: zero-sum, same as traditional HKOS play. Self-draw is
 *    worth more than a discard win (all three losers pay, vs. only the
 *    discarder under 'discarderPaysAll'), and the dealer's side of the
 *    transaction can be doubled. Every settlement is asserted to sum to zero.
 *  - Fun mode: only the round winner ever gains points; every other player's
 *    delta is exactly zero, and a draw pays nobody at all regardless of
 *    tenpai status. Every settlement is asserted to have no negative delta —
 *    that is the invariant this mode exists to protect, in place of the
 *    zero-sum one.
 *
 * A settlement bug that fabricates or drops points is the worst failure mode
 * for either mode — it undermines trust in the leaderboard — so both
 * invariants fail loudly (throw) rather than silently.
 */

import type {
  DrawContext,
  Payment,
  PlayerId,
  ScoreResult,
  WinContext,
} from '../types';
import type { HkosSettings } from './settings';

export function settleWin(
  result: ScoreResult,
  ctx: WinContext,
  players: PlayerId[],
  settings: HkosSettings,
): Payment[] {
  if (!result.meetsMinimum) {
    return toZeroSumPayments(new Map(players.map((p) => [p, 0])));
  }
  return settings.mode === 'fun'
    ? settleFunWin(result, ctx, players)
    : settleSeriousWin(result, ctx, players, settings);
}

function settleSeriousWin(
  result: ScoreResult,
  ctx: WinContext,
  players: PlayerId[],
  settings: HkosSettings,
): Payment[] {
  const deltas = new Map<PlayerId, number>(players.map((p) => [p, 0]));

  const value = result.basepoints;
  const losers = players.filter((p) => p !== ctx.winner);
  const dealerDouble = settings.serious.dealerDoublePayout;

  const multiplier = (payer: PlayerId): number => {
    if (!dealerDouble) return 1;
    // The dealer can never be both winner and payer, so these don't stack.
    return ctx.winner === ctx.dealer || payer === ctx.dealer ? 2 : 1;
  };

  const charge = (payer: PlayerId, units: number) => {
    const amount = value * units * multiplier(payer);
    deltas.set(payer, (deltas.get(payer) ?? 0) - amount);
    deltas.set(ctx.winner, (deltas.get(ctx.winner) ?? 0) + amount);
  };

  if (ctx.selfDraw) {
    for (const p of losers) charge(p, 1);
  } else if (settings.serious.discardPayment === 'discarderPaysAll') {
    if (!ctx.discarder) {
      throw new Error('Discard win requires ctx.discarder');
    }
    charge(ctx.discarder, 1);
  } else {
    if (!ctx.discarder) {
      throw new Error('Discard win requires ctx.discarder');
    }
    for (const p of losers) charge(p, p === ctx.discarder ? 2 : 1);
  }

  return toZeroSumPayments(deltas);
}

function settleFunWin(
  result: ScoreResult,
  ctx: WinContext,
  players: PlayerId[],
): Payment[] {
  const deltas = new Map<PlayerId, number>(players.map((p) => [p, 0]));
  deltas.set(ctx.winner, (deltas.get(ctx.winner) ?? 0) + result.basepoints);
  return toNonNegativePayments(deltas);
}

export function settleDrawRound(
  ctx: DrawContext,
  players: PlayerId[],
  settings: HkosSettings,
): Payment[] {
  if (settings.mode === 'fun') {
    // Fun mode's "nobody ever loses points" promise applies to draws too —
    // nobody was one tile closer to a payout than anyone else in a mode with
    // no payouts between players, so tenpai status is simply not scored.
    return toNonNegativePayments(new Map(players.map((p) => [p, 0])));
  }
  return settleSeriousDraw(ctx, players, settings);
}

function settleSeriousDraw(
  ctx: DrawContext,
  players: PlayerId[],
  settings: HkosSettings,
): Payment[] {
  const deltas = new Map<PlayerId, number>(players.map((p) => [p, 0]));
  if (!settings.serious.tenpaiPaymentOnDraw) return toZeroSumPayments(deltas);

  const ready = players.filter((p) => ctx.tenpai.includes(p));
  const notReady = players.filter((p) => !ctx.tenpai.includes(p));
  if (ready.length === 0 || notReady.length === 0) return toZeroSumPayments(deltas);

  const unit = settings.serious.tenpaiPaymentUnits * settings.serious.baseUnit;
  for (const payer of notReady) {
    for (const receiver of ready) {
      deltas.set(payer, (deltas.get(payer) ?? 0) - unit);
      deltas.set(receiver, (deltas.get(receiver) ?? 0) + unit);
    }
  }

  return toZeroSumPayments(deltas);
}

function toZeroSumPayments(deltas: Map<PlayerId, number>): Payment[] {
  const payments = [...deltas].map(([player, delta]) => ({ player, delta }));
  const sum = payments.reduce((a, p) => a + p.delta, 0);
  if (sum !== 0) {
    throw new Error(
      `Settlement does not balance (sum=${sum}). This is a scoring bug: ${JSON.stringify(payments)}`,
    );
  }
  return payments;
}

function toNonNegativePayments(deltas: Map<PlayerId, number>): Payment[] {
  const payments = [...deltas].map(([player, delta]) => ({ player, delta }));
  const negative = payments.filter((p) => p.delta < 0);
  if (negative.length > 0) {
    throw new Error(
      `Fun mode settlement produced a negative delta — this is a scoring bug: ${JSON.stringify(negative)}`,
    );
  }
  return payments;
}
